import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import TableLayout, {
  TableActionTD,
  TableRemoveRow,
  TableTD,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { ListItem, ListItemPhoneNumber, ListItemStatus } from 'dashboard/models/list-item';
import { User } from 'dashboard/models/user';
import { SortableAttributeColumn } from 'dashboard/views/DashboardListsShowView/ListItemsTable';
import {
  LIST_ITEM_STATUS_LABEL_MAP,
  LIST_ITEM_STATUS_MAP,
} from 'dashboard/views/DashboardListsShowView/ListItemTypes/constants';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

const PhoneNumberTable = styled('div')`
  margin-bottom: ${SPACINGS.base}px;
`;

interface PhoneNumberTableProps {
  generateHeaders: (columns: SortableAttributeColumn[]) => JSX.Element;
  listActive: boolean;
  isLoading: boolean;
  users: User[];
  listItems: ListItemPhoneNumber[];
  dataTest?: string;
  removeListItem: (listItem: ListItem) => void;
  nullState: ReactNode;
}

const PhoneNumber = ({
  generateHeaders,
  listActive,
  isLoading,
  users,
  listItems,
  dataTest,
  removeListItem,
  nullState,
}: PhoneNumberTableProps) => {
  const phoneNumberRows = listItems.map((phoneNumber, key) => {
    const creator =
      phoneNumber.relationships.creator.data != null
        ? (users.find(
            (apiModel) => apiModel.id === phoneNumber.relationships.creator.data.id,
          ) as User)
        : undefined;
    return (
      <PhoneNumberTableRow
        key={key}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creator={creator}
        listActive={listActive}
        phoneNumber={phoneNumber}
        removeListItem={removeListItem}
      />
    );
  });

  return (
    <PhoneNumberTable data-test={dataTest}>
      <TableLayout
        dataTest='phone-number-table__table'
        isLoading={isLoading}
        isEmpty={listItems.length === 0}
        nullState={nullState}
        showHeader={true}
        headerTR={generateHeaders([
          { key: 'value', label: 'Phone number', sortable: true },
          { key: 'match-count', label: '# Matches', sortable: true },
          { key: 'creator', label: 'Added by', sortable: false },
          { key: 'created-at', label: 'Created at', sortable: true, timezone: true },
          { key: 'status', label: 'Status', sortable: true },
          { key: 'archive', label: '', sortable: false },
        ])}
      >
        {phoneNumberRows}
      </TableLayout>
    </PhoneNumberTable>
  );
};

interface PhoneNumberTableRowProps {
  creator: User;
  listActive: boolean;
  phoneNumber: ListItemPhoneNumber;
  removeListItem: (listItem: ListItem) => void;
}

const PhoneNumberTableRow = ({
  creator,
  listActive,
  phoneNumber,
  removeListItem,
}: PhoneNumberTableRowProps) => {
  const {
    value,
    status,
    'created-at': createdAt,
    'match-count': matchCount,
    'redacted-at': redactedAt,
  } = phoneNumber.attributes;

  // NOTE: list items imported via API will not have a creator
  const nameFull = userDisplayName(creator);

  return (
    <TableTR data-test={`phone-number-table__row--${phoneNumber.id}`} id='phone-number-table__row'>
      <TableTD>{redactedAt ? <Sticker id={phoneNumber.id}>Redacted</Sticker> : value}</TableTD>
      <TableTD>{matchCount}</TableTD>
      <TableTD>{nameFull}</TableTD>
      <TableTD>{createdAt && DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>
        {status && (
          <Sticker variant={LIST_ITEM_STATUS_MAP[status]}>
            {LIST_ITEM_STATUS_LABEL_MAP[status]}
          </Sticker>
        )}
      </TableTD>
      {listActive && status === ListItemStatus.Active ? (
        <TableActionTD>
          <TableRemoveRow
            onClick={() => removeListItem(phoneNumber)}
            id={`phone-number-table-row__delete--${phoneNumber.id}`}
            data-test={`phone-number-table-row__delete--${phoneNumber.id}`}
          />
        </TableActionTD>
      ) : (
        <TableTD />
      )}
    </TableTR>
  );
};

export default PhoneNumber;
